import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';

type SparkleData = {
  id: string;
  x: string;
  y: string;
  color: string;
  delay: number;
  scale: number;
  lifespan: number;
};

type SparklesProps = {
  children: React.ReactNode;
  className?: string;
  count?: number;
  colors?: { first: string; second: string };
};

const DEFAULT_COLORS = { first: '#facc15', second: '#f97316' };

function getResponsiveCount(base: number) {
  if (typeof window === 'undefined') return base;
  const width = window.innerWidth;
  if (width < 640) return Math.max(4, Math.round(base * 0.45));
  if (width < 1024) return Math.max(6, Math.round(base * 0.7));
  return base;
}

export function Sparkles({ children, className = '', count = 10, colors = DEFAULT_COLORS }: SparklesProps) {
  const [sparkles, setSparkles] = useState<SparkleData[]>([]);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof window === 'undefined') return undefined;
    if (!('IntersectionObserver' in window)) {
      setIsInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setIsInView(entry.isIntersecting), {
      rootMargin: '120px',
      threshold: 0,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isInView) return undefined;
    const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    // Reporte la première génération hors du chemin critique (post FCP)
    let warmupHandle: number | null = null;
    let interval: number | null = null;
    let resizeFrame = 0;

    const generate = (): SparkleData => ({
      id: `${Math.random().toString(36).slice(2)}-${Date.now()}`,
      x: `${Math.random() * 100}%`,
      y: `${Math.random() * 100}%`,
      color: Math.random() > 0.5 ? colors.first : colors.second,
      delay: Math.random() * 4,
      scale: Math.random() * 0.6 + 0.35,
      lifespan: Math.random() * 12 + 8,
    });

    const handleResize = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        setSparkles(Array.from({ length: getResponsiveCount(count) }, generate));
      });
    };

    const start = () => {
      const responsiveCount = getResponsiveCount(count);
      setSparkles(Array.from({ length: responsiveCount }, generate));
      window.addEventListener('resize', handleResize);
      interval = window.setInterval(() => {
        setSparkles((current) => current.map((s) => (s.lifespan <= 0 ? generate() : { ...s, lifespan: s.lifespan - 0.1 })));
      }, 220);
    };

    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof idle === 'function') {
      warmupHandle = idle(start, { timeout: 1200 });
    } else {
      warmupHandle = window.setTimeout(start, 200) as unknown as number;
    }

    return () => {
      if (interval !== null) window.clearInterval(interval);
      window.removeEventListener('resize', handleResize);
      window.cancelAnimationFrame(resizeFrame);
      if (warmupHandle !== null) {
        const cancelIdle = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
        if (typeof cancelIdle === 'function') cancelIdle(warmupHandle);
        else clearTimeout(warmupHandle);
      }
    };
  }, [count, colors.first, colors.second, isInView]);

  return (
    <span ref={wrapperRef} className={`relative inline-block ${className}`.trim()}>
      {isInView && sparkles.map((sparkle) => (
        <Sparkle key={sparkle.id} {...sparkle} />
      ))}
      <span className="relative">{children}</span>
    </span>
  );
}

function Sparkle({ x, y, color, delay, scale }: SparkleData) {
  return (
    <motion.svg
      aria-hidden="true"
      className="pointer-events-none absolute z-20 w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5"
      initial={{ opacity: 0, left: x, top: y }}
      animate={{ opacity: [0, 1, 0], scale: [0, scale, 0], rotate: [60, 120, 180] }}
      transition={{ duration: 1.8, repeat: Infinity, delay, ease: 'easeInOut' }}
      viewBox="0 0 21 21"
    >
      <path
        d="M9.82531 0.843845C10.0553 0.215178 10.9446 0.215178 11.1746 0.843845L11.8618 2.72026C12.4006 4.19229 12.3916 6.39157 13.5 7.5C14.6084 8.60843 16.8077 8.59935 18.2797 9.13822L20.1561 9.82534C20.7858 10.0553 20.7858 10.9447 20.1561 11.1747L18.2797 11.8618C16.8077 12.4007 14.6084 12.3916 13.5 13.5C12.3916 14.6084 12.4006 16.8077 11.8618 18.2798L11.1746 20.1562C10.9446 20.7858 10.0553 20.7858 9.82531 20.1562L9.13819 18.2798C8.59932 16.8077 8.60843 14.6084 7.5 13.5C6.39157 12.3916 4.19225 12.4007 2.72023 11.8618L0.843814 11.1747C0.215148 10.9447 0.215148 10.0553 0.843814 9.82534L2.72023 9.13822C4.19225 8.59935 6.39157 8.60843 7.5 7.5C8.60843 6.39157 8.59932 4.19229 9.13819 2.72026L9.82531 0.843845Z"
        fill={color}
      />
    </motion.svg>
  );
}
